import { useCallback } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { useGateOverride } from 'modules/common/integrations/Statsig/FeatureGateOverrides';

/**
 * Imperative, override-aware counterpart to `useStatsigGate`.
 *
 * Use this only when a gate can't be read with one `useStatsigGate` call per gate — i.e. the
 * gate name isn't known at author time, gates are evaluated in a loop, or a `checkGate`
 * function has to be passed down (menu/nav filtering, launchpad schema validators). Returns a
 * `(gateName) => boolean` that applies any Feature Gate Override Manager override first and
 * otherwise falls back to the real Statsig value — so the override manager works in these
 * places too. For a single known gate, prefer `useStatsigGate`.
 */
export default function useCheckGate() {
    const {
        client: { checkGate },
    } = useStatsigClient();
    const resolveOverride = useGateOverride();

    return useCallback(
        (gateName: string): boolean => {
            const overrideValue = resolveOverride(gateName);
            if (overrideValue !== null) {
                return overrideValue;
            }
            return checkGate(gateName);
        },
        [checkGate, resolveOverride]
    );
}
